import mixpanel from 'mixpanel-browser'
import * as Sentry from '@sentry/vue'
import { Crisp } from 'crisp-sdk-web'
import _ from 'lodash'

import config from '@/config.js'
import parseEnvironment from '@/lib/parseEnvironment'
import UserAuthentication from '@/auth'

const MIXPANEL_TOKEN = config.mixpanelToken
const MIXPANEL_WEB_APP_VERSION = '2.0'
const TRACKING_STATE_KEY = '__reverTrackingState__'
const MIXPANEL_SUPER_PROPERTIES = {
  accessSource: 'web',
  version: MIXPANEL_WEB_APP_VERSION,
}

const TrackingPlugin = {
  install(Vue) {
    Vue.prototype.$configureTracking = function() {
      try {
        if (trackingIsEnabled(this.$store)) {
          this.$enableTracking()
        } else {
          this.$enableAnonymousTracking()
        }
      } catch (error) {
        console.error('Error configuring tracking', error)
      }
    }

    Vue.prototype.$enableTracking = function() {
      this.$initSupportChat()

      const userIsLoggedIn = new UserAuthentication(
        window.localStorage
      ).isLoggedIn()
      const account = this.$store.getters.account

      if (userIsLoggedIn) setupMixpanel(account)
      if (config.env !== 'localhost') setupSentry(Vue, account)
      if (config.env !== 'localhost') setupJimo(account)
      verifyClaritySetup()
    }

    Vue.prototype.$enableAnonymousTracking = function() {
      this.$store.dispatch('setAnonymousTracking', true)

      this.$initSupportChat()
      setupMixpanelAnonymously()
      disableClarity()

      if (config.env !== 'localhost') setupSentry(Vue, {})
    }

    Vue.prototype.$resetTracking = function() {
      try {
        resetMixpanel()
        resetJimo()
        resetCrisp()
      } catch (error) {
        console.error('Error resetting tracking', error)
      }
    }

    Vue.prototype.$initSupportChat = function() {
      try {
        const shouldDisableSupportChat = Boolean(
          this.$store?.getters?.organization?.hasIntercomDisabled
        )

        if (shouldDisableSupportChat) {
          Crisp.chat.hide()
          return
        }

        setupCrisp(this.$store)
      } catch (error) {
        console.error('Error setting up support chat', error)
      }
    }

    Vue.prototype.$track = function(eventName, store, data) {
      if (trackingIsEnabled(store)) {
        this.$trackMixpanel(eventName, data)
      }
    }

    Vue.prototype.$trackMixpanel = function(eventName, data) {
      ensureMixpanelInitialized()
      mixpanel.track(eventName, data)
    }

    Vue.prototype.$openSupportChat = function(message) {
      const shouldDisableSupportChat = Boolean(
        this.$store?.getters?.organization?.hasIntercomDisabled
      )

      if (shouldDisableSupportChat) return

      Crisp.message.show('text', message)
    }
  },
}

function trackingIsEnabled(store) {
  if (!store?.getters?.account?.email) {
    return store?.getters?.anonymousTracking
  }

  return Boolean(store?.getters?.account?.shareAnalytics)
}

function setupCrisp(store) {
  try {
    const trackingState = getTrackingState()
    const account = store?.getters?.account
    const isAnonymousUser = !store?.getters?.account?.shareAnalytics

    Crisp.configure(config.crispWebsiteId, {
      autoload: false,
      locale: isAnonymousUser ? null : account?.language,
    })

    if (isAnonymousUser) {
      Crisp.setTokenId(null)
      Crisp.user.setEmail(null)
      Crisp.user.setNickname(null)
      Crisp.user.setCompany(null)
    } else {
      // Crisp API only supports string, number and boolean values in these attributes, otherwise an error is returned, which might cause users not being properly identified. Here we remove `null` and `undefined` values.
      const profileData = _.omitBy(
        {
          environment: parseEnvironment(config.reverEnv),
          organizationId:
            account.organization?._id ?? account.organization ?? null,
          siteId: account.site?._id ?? account.site ?? null,
          site: account.site?.name ?? null,
          organization: account.organization?.name ?? null,
          organizationType: account.organization?.type ?? null,
          joinedAt: account.joinedAt || null,
          isAdmin: account.isAdmin,
          isOrganizationAdmin: account.isOrganizationAdmin,
          isSiteAdmin: account.isSiteAdmin,
          isCoach: account.isCoach,
          isRoutineManager: account.isRoutineManager,
          isImpactValidator: account.canDoFinancialValidation,
          isReverAIAdmin: account.isReverAIAdmin,
          isOrganizationAIManager: account.isOrganizationAIManager,
          canUseAIFeatures: account.canUseAIFeatures,
          aiMonthlyTokenLimit: account.aiMonthlyTokenLimit || null,
          position: account.position || null,
          language: account.language ?? 'en',
        },
        _.isNil
      )

      Crisp.setTokenId(account?._id ?? null)
      Crisp.user.setEmail(account?.email)
      Crisp.user.setNickname(store?.getters?.accountFullName)
      Crisp.user.setCompany(account?.organization?.name)
      Crisp.session.setData(profileData)
    }

    // We still refresh Crisp user/session data on every call. Only the SDK load
    // itself is guarded so repeated setup calls do not inject the widget twice.
    if (trackingState.crispLoaded) return

    Crisp.load()
    trackingState.crispLoaded = true
  } catch (error) {
    console.error('Error setting up Crisp', error)
  }
}

function setupMixpanel(account) {
  try {
    if (!account) return

    ensureMixpanelInitialized()

    const {
      isAdmin,
      isOrganizationAdmin,
      isCoach,
      isSiteAdmin,
      isRoutineManager,
      canDoFinancialValidation,
    } = account

    const superProperties = {
      accessSource: 'web',
      version: MIXPANEL_WEB_APP_VERSION,
      organizationId: account.organization?._id ?? account.organization ?? null,
      siteId: account.site?._id ?? account.site ?? null,
      siteName: account.site?.name ?? null,
      organization: account.organization?.name ?? null,
      organizationType: account.organization?.type ?? null,
      organizationPlan: account.organization?.plan ?? null,
      joinedAt: account.joinedAt || null,
      isAdmin,
      isOrganizationAdmin,
      isRoutineManager,
      isImpactValidator: canDoFinancialValidation,
      jobTitle: account.position || null,
      isCoach,
      isSiteAdmin,
      isOnlyUser: !isAdmin && !isOrganizationAdmin && !isCoach && !isSiteAdmin,
    }

    mixpanel.identify(account._id)
    mixpanel.people.set(superProperties)
    mixpanel.register(superProperties)
    mixpanel.track('session.Start', { status: 'success' })
  } catch (error) {
    console.error('Error setting up Mixpanel', error)
  }
}

function setupMixpanelAnonymously() {
  ensureMixpanelInitialized()
  mixpanel.register(MIXPANEL_SUPER_PROPERTIES)
}

function setupSentry(Vue, account) {
  try {
    const trackingState = getTrackingState()

    if (!trackingState.sentryInitialized) {
      Sentry.init({
        Vue,
        dsn:
          'https://b8f0017a7a1a4e75bfb55890bd7edde1@o230902.ingest.sentry.io/4504364243943424',
        environment: config.reverEnv,
        release: process.env.SENTRY_RELEASE || undefined,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate:
          config.reverEnv === 'useast1-prod01' ? 0.1 : 0.2,
        replaysOnErrorSampleRate:
          config.reverEnv === 'useast1-prod01' ? 1.0 : 0.1,
        attachProps: true,
        integrations: [
          new Sentry.Replay({
            maskAllText: true,
            blockAllMedia: true,
            networkCaptureBodies: true,
            networkDetailAllowUrls: [/^https:\/\/(.+)api\.reverscore\.net/],
            networkResponseHeaders: ['x-rever-logid', 'content-security-policy'],
          }),
          Sentry.browserTracingIntegration({
            router: globalThis.$router,
          }),
        ],
        // beforeSend callback method helps to filter error events.
        // Because it's called immediately before the event is sent to the server we can prevent the noise of unwanted and falsy errors.
        // More info on: https://docs.sentry.io/platforms/javascript/guides/vue/configuration/filtering/
        beforeSend: (event, hint) => {
          const error = hint.originalException

          if (
            // Set of rules to prevent sending events to Sentry:

            // Prevents AxiosError: Request failed with status code 401
            error?.response?.status === 401 ||
            //  Prevents AxiosError: Network Error (network errors have no response object)
            error?.code === 'ERR_NETWORK' ||
            error?.code === 'ECONNABORTED'
          ) {
            return null
          }
          return event
        },
      })

      trackingState.sentryInitialized = true
    }

    Sentry.configureScope(scope => {
      scope.setUser(account)
    })
  } catch (err) {
    console.log('Could not init Sentry', err)
  }
}

function setupJimo(account) {
  try {
    clearJimoInitRetry()
    window.jimoKill?.()

    const isLoggedInUser = Boolean(account?.email)
    const isAnonymousUser = !account?.shareAnalytics

    if (!isLoggedInUser || isAnonymousUser) return

    const fullName =
      account?.displayname && account?.familyname
        ? `${account.displayname} ${account.familyname}`
        : account?.email

    window.jimo.push(['set', 'core:language', [account.language]])
    window.jimo.push([
      'do',
      'identify',
      [
        account?._id,
        () => {
          window.jimo.push(['set', 'user:name', [fullName]])
          window.jimo.push(['set', 'user:email', [account?.email || null]])
          window.jimo.push([
            'set',
            'user:attributes',
            [
              {
                environment: parseEnvironment(config.reverEnv),
                organizationId:
                  account.organization?._id ?? account.organization ?? null,
                siteId: account.site?._id ?? account.site ?? null,
                site: account.site?.name ?? null,
                organization: account.organization?.name ?? null,
                organizationType: account.organization?.type ?? null,
                joinedAt: account.joinedAt || null,
                isAdmin: account.isAdmin,
                isOrganizationAdmin: account.isOrganizationAdmin,
                isSiteAdmin: account.isSiteAdmin,
                isCoach: account.isCoach,
                isRoutineManager: account.isRoutineManager,
                isImpactValidator: account.canDoFinancialValidation,
                position: account.position || null,
                language: account.language ?? 'en',
              },
              true,
            ],
          ])
        },
      ],
    ])

    initJimo()
  } catch (error) {
    console.error('Error setting up Jimo', error)
  }
}

function initJimo() {
  const trackingState = getTrackingState()

  trackingState.jimoInitTimeout = setTimeout(() => {
    trackingState.jimoInitTimeout = null
    // Due to race conditions, Jimo might not be ready to init. If we try to init it before it's ready, it will throw an error and users won't be properly identify, nor tours or banners will be shown, so we have to retry until it's ready.
    if (window.jimoInit) {
      window.jimoInit() // Jimo was configured to be manually initialized as described in https://help.usejimo.com/knowledge-base/for-developers/sdk-guides/manual-initialization
    } else {
      console.log('Jimo is not ready yet, trying to init again in 2 seconds...')
      initJimo()
    }
  }, 2000)
}

function resetMixpanel() {
  mixpanel.reset()
  mixpanel.register(MIXPANEL_SUPER_PROPERTIES)
}

function resetCrisp() {
  Crisp.session.reset()
}

function resetJimo() {
  clearJimoInitRetry()
  window.jimoKill?.()
}

function verifyClaritySetup() {
  // Clarity is only enabled for production
  if (config.reverEnv !== 'useast1-prod01') {
    console.log('App is not running in production, disabling Clarity...')
    disableClarity()
  }
}

function disableClarity() {
  window.clarity?.('consent', false) // this should remove all cookies
  window.clarity?.('stop') // this should stop the tracking
}

function ensureMixpanelInitialized() {
  const trackingState = getTrackingState()

  if (trackingState.mixpanelInitialized) return

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: config.env === 'localhost',
    ignore_dnt: config.env === 'localhost', // Browsers like Brave enable a feature called "Do not track" (DNT) by default, which prevents tracking. This option ignores that setting only in local environments.
  })

  trackingState.mixpanelInitialized = true
}

function clearJimoInitRetry() {
  const trackingState = getTrackingState()

  if (!trackingState.jimoInitTimeout) return

  clearTimeout(trackingState.jimoInitTimeout)
  trackingState.jimoInitTimeout = null
}

function getTrackingState() {
  const globalScope = globalThis

  if (globalScope[TRACKING_STATE_KEY]) {
    return globalScope[TRACKING_STATE_KEY]
  }

  globalScope[TRACKING_STATE_KEY] = {
    crispLoaded: false,
    jimoInitTimeout: null,
    mixpanelInitialized: false,
    sentryInitialized: false,
  }

  return globalScope[TRACKING_STATE_KEY]
}

export default TrackingPlugin
